import { useState } from 'react';
import { Box, SpaceBetween, Link } from '@cloudscape-design/components';
import { useNavigate } from 'react-router-dom';
import { ConfigurationObject } from '../../services/configs-api';
import { resolveConfigName, formatPollyVoice, formatOnOff } from './format-utils';

interface DnisDetailProps {
  config: ConfigurationObject;
  allConfigs: ConfigurationObject[];
}

/**
 * Human-readable detail view for DNIS config type.
 * Shows: Welcome Message (truncated 80 chars), Destination, Polly Voice,
 * Emergency Mode, Business Hours (linked)
 */
export function DnisDetail({ config, allConfigs }: DnisDetailProps) {
  const navigate = useNavigate();
  const payload = config.payload ?? {};
  const [expanded, setExpanded] = useState(false);

  const welcomeMessage = (payload.welcomeMessage as string) ?? '';
  const destinationType = (payload.destinationType as string) ?? '—';
  const destinationValue = (payload.destinationValue as string) ?? '—';
  const pollyVoice = payload.pollyVoice as string | undefined;
  const bhScheduleId = payload.businessHoursSchedule as string | undefined;
  const emergency = formatOnOff(payload.emergencyEnabled);

  const truncatedMessage = welcomeMessage.length > 80 && !expanded
    ? welcomeMessage.slice(0, 80) + '...'
    : welcomeMessage;

  // Resolve destination value to human-readable name if it's a config reference
  const destinationDisplay = ['queue', 'menu', 'deflect'].includes(destinationType)
    ? resolveConfigName(destinationValue, allConfigs)
    : destinationValue;

  return (
    <SpaceBetween size="m">
      <div>
        <Box variant="awsui-key-label">Welcome Message</Box>
        <Box variant="p" color="text-body-secondary">
          "{truncatedMessage}"
          {welcomeMessage.length > 80 && (
            <Link onFollow={(e) => { e.preventDefault(); setExpanded(!expanded); }}>
              {expanded ? ' Show less' : ' Show more'}
            </Link>
          )}
        </Box>
      </div>

      <div>
        <Box variant="awsui-key-label">Destination</Box>
        <div>
          <strong>{destinationType.charAt(0).toUpperCase() + destinationType.slice(1)}</strong>
          {' → '}
          {destinationDisplay}
        </div>
      </div>

      <div>
        <Box variant="awsui-key-label">Voice</Box>
        <div>{formatPollyVoice(pollyVoice)}</div>
      </div>

      <div>
        <Box variant="awsui-key-label">Emergency Mode</Box>
        <span style={{ color: emergency.colour === 'green' ? '#037F0C' : emergency.colour === 'red' ? '#D13212' : '#687078' }}>
          {emergency.text}
        </span>
      </div>

      <div>
        <Box variant="awsui-key-label">Business Hours Schedule</Box>
        {bhScheduleId ? (
          <Link onFollow={(e) => { e.preventDefault(); navigate(`/configs/${bhScheduleId}`); }}>
            {resolveConfigName(bhScheduleId, allConfigs)}
          </Link>
        ) : (
          <Box color="text-status-inactive">—</Box>
        )}
      </div>
    </SpaceBetween>
  );
}
